import Documents from "../../icons/Documents.tsx";
import Money from "../../icons/Money.tsx";
import Home from "../../icons/Home.tsx";
import Handshake from "../../icons/Handshake.tsx";
import Checkmark from "../../icons/Checkmark.tsx";
import Checklist from "../../icons/Checklist.tsx";
import CreditCard from "../../icons/CreditCard.tsx";
import User from "../../icons/UserCircle.tsx";
import Questionmark from "../../icons/Questionmark.tsx";
import { useGetQuestionnaireSectionsQuery } from "../../../api/questionnaire.ts";
import { useQuestionnaireSectionState } from "../../../hooks/useQuestionnaireSectionState.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useNotarizationState } from "../../../hooks/useNotarizationState.ts";
import { QuestionnaireSectionStatus } from "../../../../../api/src/types.ts";
import { useNegotiationState } from "../../../hooks/useNegotiationState.ts";
import useGetCurrentUserDocument from "../../../hooks/useGetCurrentUserDocument.ts";
import useCurrentUserPaymentData from "../../../hooks/useCurrentUserPaymentData.ts";
import { useGetIncomesByUserIdQuery } from "../../../api/financials/incomes.ts";
import { hasAllRequiredIncomeYears } from "../../../utils/incomeValidation.ts";

export const useNavItems = () => {
  const {
    user,
    document,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserDocument();

  const {
    isNotarizationComplete,
    isDocumentInProof,
    isDocumentFinalized,
    isDocumentReviewed,
    isLoading: isDocumentStatusLoading,
    error: userDocumentStatusError,
  } = useQuestionnaireSectionState();

  const {
    data,
    isLoading: isSectionsLoading,
    error: sectionsError,
  } = useGetQuestionnaireSectionsQuery(
    user?.id &&
      !isDocumentStatusLoading &&
      !userDocumentStatusError &&
      !isNotarizationComplete &&
      !isDocumentInProof &&
      !isDocumentFinalized &&
      !isDocumentReviewed
      ? { id: user.id, includeStatus: true }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { isPrenupPaid } = useCurrentUserPaymentData();
  const { notarizationInfoMessage, isNotarizationFileUploaded } =
    useNotarizationState();
  const isQuestionnaireComplete =
    isDocumentFinalized ||
    isDocumentReviewed ||
    isDocumentInProof ||
    isNotarizationComplete ||
    (data?.every(
      (section) =>
        section.status === QuestionnaireSectionStatus.COMPLETED ||
        section.status === QuestionnaireSectionStatus.NOT_APPLICABLE,
    ) ??
      false);

  const { data: incomes } = useGetIncomesByUserIdQuery(
    user?.id && document?.id
      ? { userId: user.id, docId: document.id }
      : skipToken,
  );

  const incomeData = incomes ?? [];
  const hasRequiredIncomeYears = hasAllRequiredIncomeYears(incomeData);

  const areAccountsLinked = !!document?.userBId;
  const {
    isLoading: isNegotiationsLoading,
    isDisabled: isNegotiationsDisabled,
    isComplete: isNegotiationsComplete,
  } = useNegotiationState({
    isQuestionnaireComplete,
    hasFinancials: hasRequiredIncomeYears,
    areAccountsLinked,
  });

  // Alignment is accessible if current user has completed their parts (partner completion checked on page itself)
  const canAccessAlignment =
    isQuestionnaireComplete && hasRequiredIncomeYears && areAccountsLinked;

  const isBubbleImportedFile =
    document?.lastGeneratedInputHash === "bubble import";

  return [
    { icon: <Home />, label: "Dashboard", path: "/" },
    {
      icon: <Checklist />,
      label: "Questionnaire",
      path: "/questionnaire",
      sections: data ?? [],
      sectionsError: sectionsError ?? userError,
      sectionsLoading: isSectionsLoading || isUserLoading,
      order: 1,
    },
    {
      icon: <Money />,
      label: "Financial Disclosure",
      path: "/financial-disclosure",
      isDisabled: !isPrenupPaid,
      order: 2,
    },
    {
      icon: <Handshake />,
      label: "Alignment",
      path: "/alignment",
      isDisabled:
        !isPrenupPaid ||
        !canAccessAlignment ||
        isNegotiationsLoading ||
        (isBubbleImportedFile && isNotarizationFileUploaded),
      order: 3,
    },
    {
      icon: <Documents />,
      label: "Documents",
      path: "/documents",
      isDisabled:
        !isPrenupPaid ||
        isNegotiationsDisabled ||
        !isNegotiationsComplete ||
        (isBubbleImportedFile && isNotarizationFileUploaded),
      order: 4,
    },
    {
      icon: <Checkmark />,
      label: "Review & Notarize",
      path: "/notarize",
      isDisabled:
        !isPrenupPaid ||
        ((!!notarizationInfoMessage || !isQuestionnaireComplete) &&
          !(isBubbleImportedFile && isNotarizationFileUploaded)),
      order: 5,
    },
  ];
};

export const bottomNavItems = [
  { icon: <CreditCard />, label: "Payment", path: "/payment" },
  { icon: <Questionmark />, label: "Help", path: "/help" },
  { icon: <User />, label: "Account", path: "/profile" },
];
